import type { z } from "zod";

/**
 * Field descriptions derived from zod v4 schemas so the editor can render a form for any
 * block / settings object without hand-writing one per type.
 */
export type FieldSpec =
  | { kind: "string"; optional: boolean; max?: number; format?: "url" | "email" }
  | { kind: "text"; optional: boolean; max?: number }
  | {
      kind: "number";
      optional: boolean;
      min?: number;
      max?: number;
      int: boolean;
      nullable: boolean;
    }
  | { kind: "boolean"; optional: boolean; default?: boolean }
  | { kind: "enum"; optional: boolean; options: (string | number)[]; default?: string | number }
  | { kind: "media"; optional: boolean }
  | { kind: "array"; optional: boolean; element: FieldSpec; min?: number; max?: number }
  | { kind: "object"; optional: boolean; fields: Record<string, FieldSpec> }
  | { kind: "literal"; optional: boolean; value: unknown }
  | { kind: "unknown"; optional: boolean };

type AnyDef = {
  type: string;
  innerType?: z.ZodType;
  defaultValue?: unknown;
  entries?: Record<string, string | number>;
  values?: unknown[];
  options?: z.ZodType[];
  element?: z.ZodType;
  shape?: Record<string, z.ZodType>;
  checks?: { _zod: { def: Record<string, unknown> & { check: string } } }[];
  format?: string;
};

function def(s: z.ZodType): AnyDef {
  return (s as unknown as { def: AnyDef }).def;
}

function checkValue(d: AnyDef, name: string, key: string): number | undefined {
  const c = d.checks?.find((x) => x._zod.def.check === name);
  const v = c?._zod.def[key];
  return typeof v === "number" ? v : undefined;
}

/** Strings over this length are edited in a textarea. */
const TEXT_THRESHOLD = 200;
const MEDIA_KEYS = ["id", "url", "alt"];
const SINGLE_LINE_KEYS = /(^|_)(href|url|src|id|code|slug|handle)$/;

export function describe(schema: z.ZodType): FieldSpec {
  let s = schema;
  let optional = false;
  let defaultValue: unknown;
  let nullable = false;
  // Unwrap optional / default / nullable wrappers.
  for (;;) {
    const d = def(s);
    if (
      (d.type === "optional" ||
        d.type === "nullable" ||
        d.type === "default" ||
        d.type === "prefault") &&
      d.innerType
    ) {
      if (d.type === "optional") optional = true;
      if (d.type === "nullable") nullable = true;
      if (d.type === "default" || d.type === "prefault") {
        optional = true;
        defaultValue = d.defaultValue;
      }
      s = d.innerType;
      continue;
    }
    break;
  }
  const d = def(s);
  switch (d.type) {
    case "string": {
      const max = checkValue(d, "max_length", "maximum");
      const format = d.format === "url" ? "url" : d.format === "email" ? "email" : undefined;
      if (!format && max !== undefined && max >= TEXT_THRESHOLD)
        return { kind: "text", optional, max };
      return { kind: "string", optional, max, format };
    }
    case "number": {
      const isInt = d.checks?.some((c) => c._zod.def.check === "number_format") ?? false;
      const gt = d.checks?.find((c) => c._zod.def.check === "greater_than")?._zod.def;
      const lt = d.checks?.find((c) => c._zod.def.check === "less_than")?._zod.def;
      const min =
        typeof gt?.value === "number" ? (gt.inclusive ? gt.value : gt.value + 1) : undefined;
      const max =
        typeof lt?.value === "number" ? (lt.inclusive ? lt.value : lt.value - 1) : undefined;
      return { kind: "number", optional, min, max, int: isInt, nullable };
    }
    case "boolean":
      return { kind: "boolean", optional, default: defaultValue as boolean | undefined };
    case "enum":
      return {
        kind: "enum",
        optional,
        options: Object.values(d.entries ?? {}),
        default: defaultValue as string | undefined,
      };
    case "literal":
      return { kind: "literal", optional, value: d.values?.[0] };
    case "union": {
      // Union of literals (e.g. columns: 2 | 3 | 4) → select.
      const opts = d.options ?? [];
      if (opts.length && opts.every((o) => def(o).type === "literal")) {
        return {
          kind: "enum",
          optional,
          options: opts.flatMap((o) => (def(o).values ?? []) as (string | number)[]),
          default: defaultValue as string | number | undefined,
        };
      }
      return { kind: "unknown", optional };
    }
    case "array": {
      const elem = d.element
        ? describe(d.element)
        : ({ kind: "unknown", optional: false } as const);
      return {
        kind: "array",
        optional,
        element: elem,
        min: checkValue(d, "min_length", "minimum"),
        max: checkValue(d, "max_length", "maximum"),
      };
    }
    case "object": {
      const shape = d.shape ?? {};
      const keys = Object.keys(shape);
      if (MEDIA_KEYS.every((k) => keys.includes(k)) && keys.includes("width"))
        return { kind: "media", optional };
      const fields: Record<string, FieldSpec> = {};
      for (const [k, v] of Object.entries(shape)) {
        const f = describe(v);
        // Long-max strings that are really identifiers / URLs stay single-line.
        fields[k] = f.kind === "text" && SINGLE_LINE_KEYS.test(k) ? { ...f, kind: "string" } : f;
      }
      return { kind: "object", optional, fields };
    }
    default:
      return { kind: "unknown", optional };
  }
}

/** A blank value satisfying the *shape* (not necessarily the constraints) of a spec. */
export function emptyValue(spec: FieldSpec): unknown {
  switch (spec.kind) {
    case "string":
    case "text":
      return spec.optional ? undefined : "";
    case "number":
      return spec.optional ? undefined : spec.nullable ? null : (spec.min ?? 0);
    case "boolean":
      return spec.default ?? false;
    case "enum":
      return spec.default ?? (spec.optional ? undefined : spec.options[0]);
    case "literal":
      return spec.value;
    case "media":
      return undefined;
    case "array":
      return spec.min && spec.min > 0
        ? Array.from({ length: spec.min }, () => emptyValue(spec.element))
        : [];
    case "object": {
      if (spec.optional) return undefined;
      return emptyObject(spec.fields);
    }
    default:
      return undefined;
  }
}

export function emptyObject(fields: Record<string, FieldSpec>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, f] of Object.entries(fields)) {
    const v = emptyValue(f);
    if (v !== undefined) out[k] = v;
  }
  return out;
}

/** Human label from a snake_case key. */
export function labelOf(key: string): string {
  const s = key
    .replace(/_/g, " ")
    .replace(/\bcta\b/gi, "CTA")
    .replace(/\bseo\b/gi, "SEO");
  return s.charAt(0).toUpperCase() + s.slice(1);
}
